import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { Receipt, ChartLineUp } from "@phosphor-icons/react";
import { PnlWorkbench } from "./PnlWorkbench";

const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const BADGE = {
  draft: "bg-zinc-100 text-zinc-600", quoted: "bg-indigo-50 text-indigo-700", blocked: "bg-rose-50 text-rose-700",
  categorizing: "bg-amber-50 text-amber-700", review: "bg-amber-50 text-amber-700", firm_review: "bg-sky-50 text-sky-700", complete: "bg-emerald-50 text-emerald-700",
};

const PAY_BADGE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200", pending: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200", expired: "bg-zinc-100 text-zinc-500 border-zinc-200", refunded: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

function PnlRevenue() {
  const [showAll, setShowAll] = useState(false);
  const { data: r } = useQuery({ queryKey: ["pnl-revenue"], queryFn: () => api.get("/pnl/revenue").then((res) => res.data), refetchInterval: 30000 });
  if (!r) return null;
  const list = showAll ? r.payments : r.payments.slice(0, 5);
  return (
    <div className="mt-4 pt-3 border-t border-zinc-100" data-testid="pnl-revenue">
      <div className="flex items-center gap-2 mb-2">
        <ChartLineUp size={14} className="text-zinc-500" />
        <h4 className="text-xs font-medium text-zinc-700">P&L Revenue</h4>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2" data-testid="pnl-revenue-quoted">
          <p className="text-[9px] uppercase tracking-wide text-zinc-400">Quoted</p>
          <p className="text-sm font-mono text-zinc-900">{fmt(r.quoted)}</p>
        </div>
        <div className="rounded-md bg-emerald-50 border border-emerald-100 p-2" data-testid="pnl-revenue-collected">
          <p className="text-[9px] uppercase tracking-wide text-emerald-600">Collected</p>
          <p className="text-sm font-mono text-emerald-700">{fmt(r.collected)}</p>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-100 p-2" data-testid="pnl-revenue-outstanding">
          <p className="text-[9px] uppercase tracking-wide text-amber-600">Outstanding</p>
          <p className="text-sm font-mono text-amber-700">{fmt(r.outstanding)}</p>
        </div>
        <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2" data-testid="pnl-revenue-rate">
          <p className="text-[9px] uppercase tracking-wide text-zinc-400">Collection rate</p>
          <p className="text-sm font-mono text-zinc-900">{r.collection_rate_pct != null ? `${r.collection_rate_pct}%` : "—"}</p>
        </div>
      </div>
      {!r.payments.length && <p className="text-[11px] text-zinc-400 italic" data-testid="pnl-revenue-empty">No payments yet — history appears here after the first Stripe checkout.</p>}
      {list.map((p) => (
        <div key={p.session_id} className="flex items-center justify-between py-1 border-b border-zinc-50 last:border-0 text-[11px]" data-testid={`pnl-payment-row-${p.session_id}`}>
          <span className="text-zinc-600 min-w-0 truncate">{p.date} · <span className="text-zinc-800">{p.client_name}</span> · {p.engagement_name}</span>
          <span className="flex items-center gap-2 shrink-0">
            <span className="font-mono text-zinc-800">{fmt(p.amount)}</span>
            <span className={`text-[9px] border rounded-sm px-1.5 py-0.5 ${PAY_BADGE[p.payment_status] || PAY_BADGE.pending}`}>{p.payment_status}</span>
          </span>
        </div>
      ))}
      {r.payments.length > 5 && (
        <button data-testid="pnl-revenue-toggle" onClick={() => setShowAll(!showAll)} className="mt-1 text-[10px] text-zinc-500 hover:text-zinc-900">
          {showAll ? "Show less" : `Show all ${r.payments.length} payments`}
        </button>
      )}
    </div>
  );
}

export function PnlConsole() {
  const [openId, setOpenId] = useState(null);
  const { data } = useQuery({ queryKey: ["pnl-console"], queryFn: () => api.get("/pnl/engagements/all").then((r) => r.data), refetchInterval: 30000 });
  const list = data?.engagements || [];
  const t = data?.totals || {};
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pnl-console">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Receipt size={16} /> Instant P&L Console</h3>
        <div className="flex gap-4 text-[11px] text-zinc-500">
          <span data-testid="pnl-console-count"><strong className="text-zinc-900">{t.count || 0}</strong> engagements</span>
          <span data-testid="pnl-console-fees">quoted <strong className="text-zinc-900">{fmt(t.fees)}</strong></span>
          <span data-testid="pnl-console-accepted">accepted <strong className="text-emerald-700">{fmt(t.accepted_fees)}</strong></span>
          {t.open_exceptions > 0 && <span className="text-amber-700"><strong>{t.open_exceptions}</strong> open exceptions</span>}
        </div>
      </div>
      {!list.length && <p className="text-xs text-zinc-400 italic">No Instant P&L engagements yet.</p>}
      <div className="space-y-1">
        {list.map((e) => (
          <button key={e.id} onClick={() => setOpenId(openId === e.id ? null : e.id)}
            className={`w-full flex items-center justify-between py-1.5 border-b border-zinc-50 last:border-0 text-xs text-left hover:bg-zinc-50 rounded-sm px-1 ${openId === e.id ? "bg-zinc-50" : ""}`}
            data-testid={`pnl-console-row-${e.id}`}>
            <div className="min-w-0">
              <span className="text-zinc-800">{e.client_name}</span>
              <span className="text-zinc-400"> · {e.name} · Form {e.entity_type} · TY {e.tax_year}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {e.quote && <span className="text-zinc-500 font-mono">{e.quote.billable_lines} ln · {fmt(e.quote.total)}</span>}
              {e.exceptions > 0 && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1.5 py-0.5">{e.exceptions} exc</span>}
              <span className={`text-[10px] px-2 py-0.5 rounded-sm ${BADGE[e.status] || BADGE.draft}`}>{e.status === "firm_review" ? "cpa review" : e.status}</span>
            </div>
          </button>
        ))}
      </div>
      {openId && <PnlWorkbench engId={openId} onClose={() => setOpenId(null)} />}
      <PnlRevenue />
    </div>
  );
}
